import { Button, Typography } from "@/components/atoms";
import { CopyTileIcon, InfoIcon } from "@/components/atoms/icons";
import { FullScreenLoader, OkDialog } from "@/components/molecules";
import { Toast } from "@/components/molecules/Toast";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useAppStore } from "@/core/stores/appStore";
import { mapInstallmentBankDetailsToAccount } from "@/modules/properties-web/constants/bankTransferDetails";
import { useBankTransferAccount } from "@/modules/properties-web/hooks/useBankTransferAccount";
import { TOP_UP_TYPE, updateTopUpType } from "@/modules/properties-web/services/topUpService";
import * as Clipboard from "expo-clipboard";
import { router, useLocalSearchParams } from "expo-router";
import type { PaymentFlowReturnTo } from "./SubmitPaymentProofScreen";
import { findInstallmentById } from "./utils/installmentUtils";
import React, { useMemo, useRef, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

function BankDetailRow({
  label,
  value,
  copyable,
  onCopied,
  onCopyError,
}: Readonly<{
  label: string;
  value: string;
  copyable?: boolean;
  onCopied?: () => void;
  onCopyError?: (error: unknown) => void;
}>) {
  const colors = useUIColors();

  const handleCopy = async () => {
    try {
      await Clipboard.setStringAsync(value);
      onCopied?.();
    } catch (error) {
      logger.error("BankTransferDetailsScreen", "Failed to copy bank detail", error);
      onCopyError?.(error);
    }
  };

  return (
    <View style={[styles.detailRow, { borderBottomColor: colors.backgroundLevel4 }]}>
      <View style={styles.detailTextContent}>
        <Typography variant="caption" color="tertiary" style={styles.detailLabel}>
          {label.toUpperCase()}
        </Typography>
        <Typography variant="headline" color="primary">
          {value || "—"}
        </Typography>
      </View>
      {copyable && value ? (
        <Pressable onPress={handleCopy} hitSlop={8} accessibilityRole="button">
          <CopyTileIcon />
        </Pressable>
      ) : null}
    </View>
  );
}

export function BankTransferDetailsScreen() {
  const { propertyId, unitLabel, returnTo, viewOnly: viewOnlyParam, paymentId } =
    useLocalSearchParams<{
      propertyId?: string;
      unitLabel?: string;
      returnTo?: PaymentFlowReturnTo;
      /** Owned-unit payment details: show this installment's account and Close. */
      viewOnly?: string;
      paymentId?: string;
    }>();
  const viewOnly = viewOnlyParam === "1";

  useCatalogDynatraceView(
    ALMETLAA_SCREEN.TOP_UP,
    ALMETLAA_ACTION.TOP_UP.BANK_TRANSFER_DETAILS_VIEW,
    !viewOnly,
  );
  useCatalogDynatraceView(
    DT_SCREEN.BANK_TRANSFER,
    'bank_transfer_details_viewed',
    !viewOnly,
  );
  const colors = useUIColors();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();

  // ADHA Pay Now reads profile-details. View Bank Details reads the installment.
  const profileAccount = useBankTransferAccount();
  const properties = useAppStore((state) => state.properties);
  const unitInstallments = useAppStore((state) => state.unitInstallments);
  const installmentAccount = useMemo(() => {
    if (!viewOnly || !paymentId) {
      return null;
    }
    const item = findInstallmentById(unitInstallments, properties, paymentId);
    return mapInstallmentBankDetailsToAccount(item?.bank_details);
  }, [paymentId, properties, unitInstallments, viewOnly]);
  const bankAccount = viewOnly ? installmentAccount : profileAccount;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [successToastVisible, setSuccessToastVisible] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState("");
  const [copiedToastVisible, setCopiedToastVisible] = useState(false);
  // Toast fires onHide once on mount too (starts hidden) — this guards
  // against treating that spurious call as a real "toast was shown" hide.
  const pendingSuccessNavRef = useRef(false);

  // Same composition as web's TransferProofNote — prefix + "payment proof" +
  // (connector + email), each piece Contentful-editable independently.
  // Each piece is trimmed and joined with a single space so the final text
  // reads correctly regardless of whether the CMS content has its own
  // leading/trailing spaces (e.g. prefix "...send the " + proof "payment
  // proof" with no trailing space of its own used to concatenate with no
  // space between them).
  const noteEmail = t("propertyDetails.submitProofEmail").trim();
  const noteParts = [
    t("propertyDetails.bankTransferNotePrefix").trim(),
    t("propertyDetails.bankTransferNoteProof").trim(),
  ];
  if (noteEmail.length > 0) {
    noteParts.push(t("propertyDetails.bankTransferNoteEmailConnector").trim(), noteEmail);
  }
  const noteText = noteParts.filter(Boolean).join(" ");

  const handleCopyError = (error: unknown) => {
    const apiMessage =
      error instanceof Error && error.message.trim().length > 0 ? error.message : "";
    setErrorDialogMessage(apiMessage || t("reserveUnit.submissionFailedError"));
    setErrorDialogVisible(true);
  };

  const handleMadeTheTransfer = async () => {
    if (isSubmitting) {
      return;
    }
    logger.info("BankTransferDetailsScreen: I've Made the Transfer pressed");
    setIsSubmitting(true);
    try {
      const response = await updateTopUpType(TOP_UP_TYPE.BANK_TRANSFER);
      logger.info("BankTransferDetailsScreen: updateTopUpType bank transfer response", {
        response,
      });
      const message = response.message?.trim() ?? "";
      if (message) {
        setSuccessToastMessage(message);
        pendingSuccessNavRef.current = true;
        setSuccessToastVisible(true);
      } else {
        router.push({
          pathname: "/(auth)/payment-proof-submit",
          params: { propertyId, unitLabel, method: "bankTransfer", returnTo },
        } as any);
      }
    } catch (error) {
      logger.error("BankTransferDetailsScreen: updateTopUpType bank transfer failed", error);
      const apiMessage =
        error instanceof Error && error.message.trim().length > 0 ? error.message : "";
      setErrorDialogMessage(apiMessage || t("reserveUnit.submissionFailedError"));
      setErrorDialogVisible(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <TextHeader title={t("propertyDetails.bankTransferDetailsTitle")} />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingBottom: 24 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.detailsCard,
            { backgroundColor: colors.backgroundLevel1, borderColor: colors.backgroundLevel4 },
          ]}
        >
          <Typography variant="title3" color="primary" style={styles.cardTitle}>
            {t("propertyDetails.transferToThisAccount")}
          </Typography>

          <BankDetailRow
            label={t("propertyDetails.accountName")}
            value={bankAccount?.accountName ?? ""}
          />
          <BankDetailRow
            label={t("propertyDetails.bankName")}
            value={bankAccount?.bankName ?? ""}
          />
          <BankDetailRow
            label={t("propertyDetails.accountNumber")}
            value={bankAccount?.accountNumber ?? ""}
            copyable
            onCopied={() => setCopiedToastVisible(true)}
            onCopyError={handleCopyError}
          />
          <BankDetailRow
            label={t("propertyDetails.iban")}
            value={bankAccount?.iban ?? ""}
            copyable
            onCopied={() => setCopiedToastVisible(true)}
            onCopyError={handleCopyError}
          />
          <BankDetailRow
            label={t("propertyDetails.swiftCode")}
            value={bankAccount?.swiftCode ?? ""}
            copyable
            onCopied={() => setCopiedToastVisible(true)}
            onCopyError={handleCopyError}
          />

          <View style={styles.noteRow}>
            <InfoIcon size={16} color={UI_PRIMITIVES.redLight} />
            <Typography variant="caption" color="tertiary" style={styles.noteText}>
              {noteText}
            </Typography>
          </View>
        </View>
      </ScrollView>

      <View style={[styles.footer, { paddingBottom: 16 + insets.bottom, backgroundColor: colors.backgroundLevel2 }]}>
        {viewOnly ? (
          <Button
            title={t("common.close")}
            variant="primary"
            onPress={() => router.back()}
            style={styles.closeButton}
          />
        ) : (
          <>
            <Button
              title={t("common.cancel")}
              variant="outline"
              onPress={() => router.back()}
              style={styles.cancelButton}
              disabled={isSubmitting}
            />
            <Button
              title={t("propertyDetails.iveMadeTheTransfer")}
              variant="primary"
              onPress={() => {
                void handleMadeTheTransfer();
              }}
              style={styles.footerButton}
              numberOfLines={1}
              disabled={isSubmitting}
            />
          </>
        )}
      </View>

      <OkDialog
        visible={errorDialogVisible}
        message={errorDialogMessage}
        onOk={() => setErrorDialogVisible(false)}
      />
      <Toast
        visible={copiedToastVisible}
        message={t("propertyDetails.copiedToClipboard")}
        position="top-right"
        onHide={() => setCopiedToastVisible(false)}
      />
      <Toast
        visible={successToastVisible}
        message={successToastMessage}
        onHide={() => {
          setSuccessToastVisible(false);
          if (pendingSuccessNavRef.current) {
            pendingSuccessNavRef.current = false;
            logger.info("BankTransferDetailsScreen: navigating to payment-proof-submit");
            router.push({
              pathname: "/(auth)/payment-proof-submit",
              params: { propertyId, unitLabel, method: "bankTransfer", returnTo },
            } as any);
          }
        }}
      />
      <FullScreenLoader visible={isSubmitting} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  detailsCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  // Font/Family, Font/Size/L Medium, weight 500, LH 100%, letter-spacing 0
  // (Typography's "title3" variant already gives Matter-Medium/20px/LH20).
  cardTitle: {
    marginBottom: 8,
    fontWeight: "500",
    letterSpacing: 0,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingVertical: 20,
    borderBottomWidth: 1,
  },
  detailTextContent: {
    flex: 1,
    gap: 2,
  },
  detailLabel: {
    letterSpacing: 0.5,
  },
  noteRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 16,
  },
  noteText: {
    flex: 1,
    lineHeight: 16,
  },
  footer: {
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  cancelButton: {
    flex: 0.7,
  },
  footerButton: {
    flex: 1.3,
  },
  closeButton: {
    flex: 1,
  },
});

export default BankTransferDetailsScreen;
